import { Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react'
import { VscClose } from '@react-icons/all-files/vsc/VscClose'
import type { ReactNode } from 'react'
import { cm } from '~/utils/utils'
import { HeaderContent } from './HeaderContent'

type Size = 'md' | 'sm' | 'lg' | 'xl' | '2xl' | 'xs' | '3xl' | '4xl' | '5xl' | 'full'
type ScrollBehavior = 'normal' | 'inside' | 'outside'
interface BaseModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  title: string | ReactNode
  isDismissable?: boolean
  size?: Size
  subtitle?: string
  children?: ReactNode
  scrollBehavior?: ScrollBehavior
  className?: string
  backdrop?: 'opaque' | 'transparent' | 'blur'
  parentRef?: React.RefObject<HTMLDivElement | null>
  headerBorder?: boolean
  contentClassName?: string
  bodyClassName?: string
  wrapperClassName?: string
}

function BaseModal({
  isOpen,
  onOpenChange,
  title,
  isDismissable = true,
  size = 'md',
  subtitle = undefined,
  children = null,
  scrollBehavior = 'normal',
  className = '',
  backdrop = 'opaque',
  parentRef,
  headerBorder = true,
  contentClassName = '',
  bodyClassName = '',
  wrapperClassName = ''
}: BaseModalProps) {
  const portalContainer = parentRef?.current || document.body
  return (
    <Modal
      portalContainer={portalContainer}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      hideCloseButton
      size={size}
      className={cm('bg-white rounded-xl', className)}
      scrollBehavior={scrollBehavior}
      backdrop={backdrop}
      classNames={{
        wrapper: cm('absolute h-full w-full shadow-heavy z-[999999]', wrapperClassName),
        base: 'h-full w-full z-[999999]',
        backdrop: cm('absolute h-full w-full z-[999999]', wrapperClassName),
      }}
    >
      <ModalContent
        className={cm(
          'dark:bg-neutral-900 dark:hover:bg-medium7 rounded-xl shadow-2xl ring-1 ring-slate-700/15',
          contentClassName
        )}
      >
        {closeModal => (
          <>
            <ModalHeader
              className={cm(
                'flex items-center justify-between pl-[20px] pr-[5px] dark:bg-medium7 py-0',
                headerBorder ? 'border-b-1 border-[rgba(0,0,0,0.12)]' : ''
              )}
            >
              <HeaderContent title={title} subtitle={subtitle} />
              <div className='flex items-center justify-center w-[50px] h-[50px]'>
                <div
                  className='flex items-center justify-center rounded-xl hover:bg-[#ececec] p-2 cursor-pointer'
                  onClick={() => closeModal()}
                >
                  <VscClose aria-label='Close' className='hover:bg-light' />
                </div>
              </div>
            </ModalHeader>
            <ModalBody
              className={cm(
                'p-[20px] dark:bg-medium7 h-[calc(100%-50px)] min-h-[450px]',
                bodyClassName
              )}
            >
              {children}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

export default BaseModal
